import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { Link } from "react-router-dom";
import {
    useGetFinancialStatsQuery,
    useGetPaymentIncomeStatsQuery,
    useGetWithdrawalStatsQuery,
    useGetAllUsersStatsQuery,
    useGetGameStatsQuery,
    useGetGameVipStatsQuery,
    usePayBlagoTourizmTokensMutation,
    usePayMarathonMutation,
    useCheckTreeIntegrityMutation,
    useGetTreeUserDetailsMutation,
    useVerifyStatsIntegrityMutation,
    useFixStatsIntegrityUserMutation,
} from "../api";
import { BalanceManagementSection } from "../components/BalanceManagementSection";
import { NewUsersStats } from "../components/NewUsersStats";
import { AllUsersStats } from "../components/AllUsersStats";
import { PaymentIncomeStats } from "../components/PaymentIncomeStats";
import { WithdrawalStats } from "../components/WithdrawalStats";
import { QuickActions } from "../components/QuickActions";
import { TreeIntegrityCheck } from "../components/TreeIntegrityCheck";
import { StatsIntegrityCheck } from "../components/StatsIntegrityCheck";
import { GameVipStatsPanel } from "../components/GameVipStatsPanel";
import { api } from "../api";

export const AdminDashboard: React.FC = () => {
    const { user } = useSelector((state: RootState) => state.auth);
    const roles = user?.roles || [];
    const isAdmin = roles.includes("admin");
    const isEditorOnly = roles.includes("editor") && !isAdmin;
    const skipAdmin = isEditorOnly;


    // Загрузка финансовой статистики (isLoading — общий спиннер дашборда)
    const { isLoading } = useGetFinancialStatsQuery(undefined, {
        pollingInterval: 120000,
        skip: skipAdmin,
    });

    const { data: newUsersStats } = (
        api as any
    ).endpoints.getNewUsersStats.useQuery();
    const { data: paymentIncomeStats } = useGetPaymentIncomeStatsQuery(undefined, {
        pollingInterval: 120000,
        skip: skipAdmin,
    });
    const { data: withdrawalStats } = useGetWithdrawalStatsQuery(undefined, {
        pollingInterval: 120000,
        skip: skipAdmin,
    });
    const { data: allUsersStats } = useGetAllUsersStatsQuery(undefined, {
        pollingInterval: 120000,
        skip: skipAdmin,
    });

    const { data: gameStats } = useGetGameStatsQuery(undefined, {
        skip: skipAdmin,
    });

    const {
        data: gameVipStats,
        isLoading: gameVipLoading,
        isError: gameVipError,
        refetch: refetchGameVip,
    } = useGetGameVipStatsQuery(undefined, {
        skip: skipAdmin,
        pollingInterval: 300000,
    });

    const [payTokens, { isLoading: isPayingTokens }] =
        usePayBlagoTourizmTokensMutation();
    const [payMarathon, { isLoading: isPayingMarathon }] =
        usePayMarathonMutation();
    const [checkTree, { isLoading: isCheckingTree }] =
        useCheckTreeIntegrityMutation();
    const [treeReport, setTreeReport] = useState<any>(null);
    const [
        fetchTreeUser,
        { data: treeUserData, isLoading: isLoadingTreeUser },
    ] = useGetTreeUserDetailsMutation();

    const [verifyStatsIntegrity] = useVerifyStatsIntegrityMutation();

    const [fixStatsIntegrityUser] = useFixStatsIntegrityUserMutation();
    // Храним ID пользователя для модалки (используется при открытии)
    // const { data: statsDetails } = useGetStatsIntegrityUserDetailsQuery(
    //     detailsUserId
    //         ? { user_id: detailsUserId, currency: detailsCurrency } 
    //         : (undefined as any),
    //     { skip: !detailsUserId }
    // );
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    const openDetails = (userId: number) => {
        setIsDetailsOpen(true);
        fetchTreeUser(userId);
    };
    const closeDetails = () => {
        setIsDetailsOpen(false);
    };

    // Обработчики для быстрых действий
    const handlePayTokens = async () => {
        if (window.confirm("Начислить токены победителям благо?")) {
            try {
                await payTokens({
                    user_id: 0,
                    amount: 0,
                }).unwrap();
                alert("Токены начислены");
            } catch (error: any) {
                alert("Ошибка: " + (error.data?.message || error.message));
            }
        }
    };

    const handlePayMarathon = async () => {
        if (window.confirm("Начислить за марафон?")) {
            try {
                await payMarathon().unwrap();
                alert("Начисления выполнены");
            } catch (error: any) {
                alert("Ошибка: " + (error.data?.message || error.message));
            }
        }
    };

    // Обработчик для проверки дерева
    const handleCheckTree = async () => {
        if (window.confirm("Проверить целостность реф. структуры?")) {
            try {
                const res = await checkTree().unwrap();
                setTreeReport(res.data ?? null);
            } catch (error: any) {
                alert("Ошибка: " + (error.data?.message || error.message));
            }
        }
    };

    const [isVerifyingStats, setIsVerifyingStats] = useState(false);
    const [statsIntegrityData, setStatsIntegrityData] = useState<any>(null);
    const [statsVerifyProgress, setStatsVerifyProgress] = useState(0);

    const handleFixStatsUser = async (userId: number) => {
        try {
            await fixStatsIntegrityUser(userId).unwrap();
            alert("Исправлено");
        } catch (e: any) {
            alert("Ошибка: " + (e.data?.message || e.message));
        }
    };


    const handleVerifyStatsIntegrity = async () => {
        if (
            !window.confirm(
                "Запустить проверку соответствия статистики? Это может занять несколько минут."
            )
        ) {
            return;
        }

        setIsVerifyingStats(true);
        setStatsIntegrityData(null);
        setStatsVerifyProgress(0);

        try {
            let offset = 0;
            const limit = 500;
            let allErrors: any[] = [];
            let totalUsers = 0;
            let totalProcessed = 0;

            while (true) {
                const response = await verifyStatsIntegrity({
                    offset,
                    limit,
                }).unwrap();

                totalUsers = response.data.total_users;
                totalProcessed += response.data.processed_users;
                allErrors = [...allErrors, ...response.data.errors];

                // Обновляем прогресс
                const progress = Math.round(
                    (totalProcessed / totalUsers) * 100
                );
                setStatsVerifyProgress(progress);

                // Если больше нет данных, завершаем
                if (
                    !response.data.has_more ||
                    response.data.next_offset === null
                ) {
                    break;
                }

                offset = response.data.next_offset;
            }

            // Устанавливаем финальные результаты
            setStatsIntegrityData({
                data: {
                    total_users: totalUsers,
                    processed_users: totalProcessed,
                    errors: allErrors,
                    errors_count: allErrors.length,
                    is_complete: true,
                },
            });
        } catch (error: any) {
            alert(
                "Ошибка: " +
                    (error.data?.message ||
                        error.message ||
                        "Неизвестная ошибка")
            );
        } finally {
            setIsVerifyingStats(false);
        }
    };

    if (isEditorOnly) {
        return (
            <div className="p-6 bg-white dark:bg-gray-800 shadow rounded">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
                    Добро пожаловать в панель редактора
                </h1>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                    Для вашей роли доступны только разделы контента. Перейдите к управлению новостями.
                </p>
                <Link
                    to="/dashboard/news"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
                >
                    Перейти к новостям
                </Link>
            </div>
        );
    }

    if (isLoading) {
        return <div className="p-6">Загрузка...</div>;
    }

    return (
        <div className="p-6 space-y-6">
            {newUsersStats?.data && <NewUsersStats data={newUsersStats.data} />}

            {allUsersStats?.data && <AllUsersStats data={allUsersStats.data} />}

            {!skipAdmin && (
                <GameVipStatsPanel
                    data={gameVipStats?.data}
                    fallbackCohorts={gameStats?.data}
                    isLoading={gameVipLoading}
                    isError={gameVipError}
                    onRetry={() => {
                        void refetchGameVip();
                    }}
                />
            )}

            {paymentIncomeStats?.data && (
                <PaymentIncomeStats data={paymentIncomeStats.data} />
            )}

            {withdrawalStats?.data && (
                <WithdrawalStats data={withdrawalStats.data} />
            )}

            <QuickActions
                onPayTokens={handlePayTokens}
                onPayMarathon={handlePayMarathon}
                isPayingTokens={isPayingTokens}
                isPayingMarathon={isPayingMarathon}
            />

            <TreeIntegrityCheck
                treeReport={treeReport}
                isCheckingTree={isCheckingTree}
                isLoadingTreeUser={isLoadingTreeUser}
                treeUserData={treeUserData}
                isDetailsOpen={isDetailsOpen}
                onCheckTree={handleCheckTree}
                onOpenDetails={openDetails}
                onCloseDetails={closeDetails}
            />

            <StatsIntegrityCheck
                statsIntegrityData={statsIntegrityData}
                isVerifyingStats={isVerifyingStats}
                statsVerifyProgress={statsVerifyProgress}
                onVerifyStatsIntegrity={handleVerifyStatsIntegrity}
                onFixUser={handleFixStatsUser}
                onOpenDetails={openDetails}
            />

            {/* Секция управления балансами и акциями */}
            <BalanceManagementSection />
        </div>
    );
};
